// implements Filter

import {Filter, FilterProps} from "../../Filter";
import {html} from "htm/preact";
import "../../../delta-date-picker";

interface DateWindowProps extends FilterProps {
    dateFrom: string
    dateTo: string
    onChange?: (filter: Filter) => void
}

export class DateWindow extends Filter<DateWindowProps> {

    cleared: any = { dateWindow: { dateFrom: null, dateTo: null }}

    constructor(props: any) {
        super(props);

        this.clear = this.clear.bind(this);

        if(!props.preloadedState.value){
            this.setStateValue({dateWindow: {dateFrom: null, dateTo: null}});
        }

        this.specificRadioClick = this.specificRadioClick.bind(this);
        this.anyRadioClick = this.anyRadioClick.bind(this);
        this.datePickerChangeHandler = this.datePickerChangeHandler.bind(this);
    }

    render() {
        return html`
            <div class="form-group">
                ${this.datePickerElement("from")}
                
                ${this.datePickerElement("to")}
            </div>
        `
    }

    componentDidMount() {
        for (const type of ["from", "to"]) {
            const picker = this.getPicker(type);
            picker?.addEventListener("date-change", this.datePickerChangeHandler);
            picker?.addEventListener("input", this.datePickerChangeHandler);
        }

        if (this.filtersManager) {
            this.filtersManager.addFilter(this);
        }

        const clearButton = document.querySelector("#delta-filters-clear-filter-link_" + this.id);

        clearButton?.addEventListener("click", () => {
            this.clear(undefined, false);
        });
    }

    specificRadioClick(event: Event) {

        if(event.target instanceof HTMLInputElement) {

            let container = event.target.closest(".govuk-radios")?.parentElement?.querySelector(".conditional-datepicker-container") as HTMLElement;

            if(container && container.classList.contains("hidden")) {
                container.classList.remove("hidden");
            }
            else if(container) {
                container.classList.add("hidden");
            }
        }
    }

    anyRadioClick(event: Event) {

        if(event.target instanceof HTMLInputElement) {
            let container = event.target.closest(".govuk-radios")?.parentElement?.querySelector(".conditional-datepicker-container") as HTMLElement;
            if(container && !container.classList.contains("hidden")) {
                container.classList.add("hidden");

                let containerId = container.getAttribute("id");
                if(containerId) {
                    let containsFrom = containerId.includes("from");
                    let containsTo = containerId.includes("to");

                    if(containsFrom) {
                        //clear inputs
                        this.setStateValue({
                            dateWindow: {
                                dateFrom: null,
                                dateTo: this.state.value.dateWindow.dateTo
                            }
                        }, () => this.setPickerValue("from", null));
                    }
                    else if(containsTo) {
                        this.setStateValue({
                            dateWindow: {
                                dateFrom: this.state.value.dateWindow.dateFrom,
                                dateTo: null
                            }
                        }, () => this.setPickerValue("to", null));
                    }
                }
            }
        }
    }

    datePickerChangeHandler(event: Event) {
        if (event instanceof CustomEvent || event.target instanceof HTMLInputElement) {
            queueMicrotask(() => this.updateStateFromPickers());
        }
    }

    getQueryParam(): Record<string, string> | undefined {
        const params: Record<string, string> = {};

        if(this.state.value){
            if(this.state.value.dateWindow.dateFrom) {
                params["rangeFrom"] = this.state.value.dateWindow.dateFrom;
            }

            if(this.state.value.dateWindow.dateTo) {
                params["rangeTo"] = this.state.value.dateWindow.dateTo;
            }
            return params;
        }
    }

    datePickerElement(type: string): any {
        let value = "";
        if(type == "from"){
            if(this.state.value?.dateWindow?.dateFrom){
                value = this.state.value.dateWindow.dateFrom;
            }
            else{
                value = "";
            }
        }
        else if(type == "to"){
            if(this.state.value?.dateWindow?.dateTo){
                value = this.state.value.dateWindow.dateTo;
            }
            else{
                value = "";
            }
        }

        return html`
            <div class="govuk-form-group date-window-radio-filter">
                <legend class="govuk-fieldset__legend govuk-fieldset__legend--m">
                    <h1 class="govuk-fieldset__heading">
                        Date ${type}
                    </h1>
                </legend>
            
                <div class="govuk-radios govuk-radios--small">
                    <div class="govuk-radios__item">
                        <input class="govuk-radios__input date-window-radio date-window-any" id="date-${type}-any" filterName="range-${type}" name="date-${type}" type="radio" value="date-${type}-any" aria-controls="conditional-date-${type}-specific" onclick=${this.anyRadioClick}/>
                        <label class="govuk-label govuk-radios__label" for="date-${type}-any">
                            All previous dates
                        </label>
                    </div>
                    <div class="govuk-radios__item"> 
            
                        <input class="govuk-radios__input date-window-radio" id="date-${type}-specific" name="date-${type}" type="radio" value="date-${type}-specific" aria-controls="conditional-date-${type}-specific" checked=${value !== ""} onclick=${this.specificRadioClick}/>
                        <label class="govuk-label govuk-radios__label" for="date-${type}-specific">
                            Specific previous date 
                        </label>
                    </div>
                </div>
                <div class="${value != "" ? "" : "hidden"} conditional-datepicker-container" id="conditional-date-${type}-specific">
                    <delta-date-picker
                        id="date-${type}-picker"
                        label="Date ${type}"
                        name="range${type == "from" ? "From" : "To"}"
                        value=${value}
                        end-date-picker=${type == "from" ? "date-to-picker" : undefined}
                        start-date-picker=${type == "to" ? "date-from-picker" : undefined}>
                    </delta-date-picker>
                </div>
            </div> `
    }

    clear(cb?: () => void, fullClear?: boolean) {
        this.setStateValue(JSON.parse(JSON.stringify(this.cleared)), () => {
            this.setPickerValue("from", null);
            this.setPickerValue("to", null);
            if (!fullClear) {
                this.filtersManager?.handleApply();
            }
            cb?.();
        });
    }

    reset(cb: () => void) {
        const value = Object.keys(this.initialStateValue).length === 0
            ? this.cleared
            : this.initialStateValue;
        this.setStateValue(JSON.parse(JSON.stringify(value)), () => {
            this.setPickerValue("from", value.dateWindow?.dateFrom ?? null);
            this.setPickerValue("to", value.dateWindow?.dateTo ?? null);
            cb();
        });
    }

    private getPicker(type: string): HTMLElement | null {
        return document.getElementById(`date-${type}-picker`);
    }

    private getPickerValue(type: string): string | null {
        return this.getPicker(type)?.querySelector<HTMLInputElement>('input[type="hidden"]')?.value || null;
    }

    private setPickerValue(type: string, value: string | null) {
        const picker = this.getPicker(type);
        if (!picker) {
            return;
        }
        const parts = value?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        const values = parts ? [parts[3], parts[2], parts[1]] : ["", "", ""];
        ["date", "month", "year"].forEach((part, index) => {
            const input = picker.querySelector<HTMLInputElement>(`.js-datepicker-${part}`);
            if (input) {
                input.value = values[index];
            }
        });
        picker.querySelector<HTMLInputElement>(".js-datepicker-year")
            ?.dispatchEvent(new Event("input", {bubbles: true}));
    }

    private updateStateFromPickers() {
        const pickers = ["from", "to"].map(type => this.getPicker(type));
        this.valid = pickers.every(picker => picker === null ||
            Array.from(picker.querySelectorAll<HTMLInputElement>(".govuk-date-input__input"))
                .every(input => input.checkValidity()));
        this.filtersManager?.toggleApplyError(!this.valid);
        this.setStateValue({
            dateWindow: {
                dateFrom: this.getPickerValue("from"),
                dateTo: this.getPickerValue("to")
            }
        });
    }
}